import { Router } from "express";
import { db } from "@workspace/db";
import { complianceItemsTable, tendersTable, usersTable } from "@workspace/db";
import { eq, and } from "drizzle-orm";
import { requireAuth, type AuthRequest } from "../lib/auth";
import { broadcastTenderUpdate } from "../lib/socket";
import { logAudit } from "../lib/activity";
import { sendMail } from "../lib/mailer";

const router = Router();

async function verifyTenderOwner(tenderId: number, companyId: number) {
  const rows = await db
    .select({ id: tendersTable.id })
    .from(tendersTable)
    .where(and(eq(tendersTable.id, tenderId), eq(tendersTable.companyId, companyId)))
    .limit(1);
  return rows[0] ?? null;
}

async function verifyItemOwner(itemId: number, companyId: number) {
  const rows = await db
    .select({ id: complianceItemsTable.id, tenderId: complianceItemsTable.tenderId })
    .from(complianceItemsTable)
    .innerJoin(tendersTable, eq(complianceItemsTable.tenderId, tendersTable.id))
    .where(and(eq(complianceItemsTable.id, itemId), eq(tendersTable.companyId, companyId)))
    .limit(1);
  return rows[0] ?? null;
}

router.get("/tenders/:id/compliance", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const companyId = req.companyId;
    if (!companyId) return res.status(400).json({ error: "No company profile" });
    if (!await verifyTenderOwner(Number(req.params.id), companyId))
      return res.status(404).json({ error: "Tender not found" });

    const items = await db
      .select()
      .from(complianceItemsTable)
      .where(eq(complianceItemsTable.tenderId, Number(req.params.id)));
    res.json(items);
  } catch (err) {
    next(err);
  }
});

router.post("/tenders/:id/compliance", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const companyId = req.companyId;
    if (!companyId) return res.status(400).json({ error: "No company profile" });
    const tenderId = Number(req.params.id);
    if (!await verifyTenderOwner(tenderId, companyId))
      return res.status(404).json({ error: "Tender not found" });

    const item = await db
      .insert(complianceItemsTable)
      .values({ ...req.body, tenderId })
      .returning();
    broadcastTenderUpdate(tenderId, "compliance:update", { action: "created", item: item[0] });
    logAudit({
      userId: req.dbUser!.id,
      userEmail: req.dbUser!.email,
      companyId,
      tenderId,
      action: "create",
      entityType: "compliance_item",
      entityId: item[0].id,
      after: item[0] as Record<string, unknown>,
    });
    res.status(201).json(item[0]);
  } catch (err) {
    next(err);
  }
});

router.patch("/compliance/:itemId", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const companyId = req.companyId;
    if (!companyId) return res.status(400).json({ error: "No company profile" });
    const existing = await verifyItemOwner(Number(req.params.itemId), companyId);
    if (!existing) return res.status(404).json({ error: "Not found" });

    const updated = await db
      .update(complianceItemsTable)
      .set({ ...req.body, updatedAt: new Date() })
      .where(eq(complianceItemsTable.id, Number(req.params.itemId)))
      .returning();
    if (!updated[0]) return res.status(404).json({ error: "Not found" });
    broadcastTenderUpdate(existing.tenderId, "compliance:update", { action: "updated", item: updated[0] });
    logAudit({
      userId: req.dbUser!.id,
      userEmail: req.dbUser!.email,
      companyId,
      tenderId: existing.tenderId,
      action: "update",
      entityType: "compliance_item",
      entityId: updated[0].id,
      after: updated[0] as Record<string, unknown>,
    });
    res.json(updated[0]);
  } catch (err) {
    next(err);
  }
});

router.delete("/compliance/:itemId", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const companyId = req.companyId;
    if (!companyId) return res.status(400).json({ error: "No company profile" });
    const existing = await verifyItemOwner(Number(req.params.itemId), companyId);
    if (!existing) return res.status(404).json({ error: "Not found" });

    await db
      .delete(complianceItemsTable)
      .where(eq(complianceItemsTable.id, Number(req.params.itemId)));
    broadcastTenderUpdate(existing.tenderId, "compliance:update", { action: "deleted", itemId: Number(req.params.itemId) });
    logAudit({
      userId: req.dbUser!.id,
      userEmail: req.dbUser!.email,
      companyId,
      tenderId: existing.tenderId,
      action: "delete",
      entityType: "compliance_item",
      entityId: Number(req.params.itemId),
    });
    res.status(204).send();
  } catch (err) {
    next(err);
  }
});

router.post("/compliance/:itemId/assign", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const companyId = req.companyId;
    if (!companyId) return res.status(400).json({ error: "No company profile" });
    const existing = await verifyItemOwner(Number(req.params.itemId), companyId);
    if (!existing) return res.status(404).json({ error: "Not found" });

    const { userId, userName, userEmail } = req.body;
    const updated = await db
      .update(complianceItemsTable)
      .set({ assignedToUserId: userId, assignedToName: userName, updatedAt: new Date() })
      .where(eq(complianceItemsTable.id, Number(req.params.itemId)))
      .returning();
    if (!updated[0]) return res.status(404).json({ error: "Not found" });
    broadcastTenderUpdate(existing.tenderId, "compliance:update", { action: "assigned", item: updated[0] });
    logAudit({
      userId: req.dbUser!.id,
      userEmail: req.dbUser!.email,
      companyId,
      tenderId: existing.tenderId,
      action: "assign",
      entityType: "compliance_item",
      entityId: updated[0].id,
      after: { assignedToUserId: userId, assignedToName: userName },
    });

    // Notify the assignee by email if they have an email address — fire-and-forget
    if (userEmail && userEmail !== req.dbUser!.email) {
      const tender = await db
        .select({ title: tendersTable.title })
        .from(tendersTable)
        .where(eq(tendersTable.id, existing.tenderId))
        .limit(1);

      const assigneeLang = userId
        ? await db.select({ language: usersTable.language }).from(usersTable).where(eq(usersTable.id, Number(userId))).limit(1)
        : [];
      const isAr = assigneeLang[0]?.language === "ar";
      const requirement = updated[0].requirement ?? "";
      const tenderTitle = tender[0]?.title ?? "";
      sendMail({
        to: userEmail as string,
        subject: isAr
          ? `تم تكليفك ببند امتثال – ${tenderTitle}`
          : `Compliance Item Assigned to You – ${tenderTitle}`,
        html: isAr
          ? `<div dir="rtl" style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto">
              <h2 style="color:#1e40af">تم تكليفك ببند امتثال</h2>
              <p>المناقصة: <strong>${tenderTitle}</strong></p>
              <p>البند: <strong>${requirement}</strong></p>
              <p>قام بالتكليف: ${req.dbUser!.firstName ?? req.dbUser!.email}</p>
              <p style="color:#6b7280;font-size:12px;margin-top:32px">AI Tender Copilot Saudi</p>
            </div>`
          : `<div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto">
              <h2 style="color:#1e40af">Compliance Item Assigned to You</h2>
              <p>Tender: <strong>${tenderTitle}</strong></p>
              <p>Requirement: <strong>${requirement}</strong></p>
              <p>Assigned by: ${req.dbUser!.firstName ?? req.dbUser!.email}</p>
              <p style="color:#6b7280;font-size:12px;margin-top:32px">AI Tender Copilot Saudi</p>
            </div>`,
      }).catch(() => {});
    }
    res.json(updated[0]);
  } catch (err) {
    next(err);
  }
});

router.get("/tenders/:id/compliance/export", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const companyId = req.companyId;
    if (!companyId) return res.status(400).json({ error: "No company profile" });
    if (!await verifyTenderOwner(Number(req.params.id), companyId))
      return res.status(404).json({ error: "Tender not found" });

    const items = await db
      .select()
      .from(complianceItemsTable)
      .where(eq(complianceItemsTable.tenderId, Number(req.params.id)));

    const header = "ID,Requirement,Category,Status,Owner,Notes\n";
    const rows = items
      .map((i) =>
        [i.id, `"${i.requirement}"`, i.category, i.status, i.owner || "", `"${i.notes || ""}"`].join(","),
      )
      .join("\n");

    res.setHeader("Content-Type", "text/csv");
    res.setHeader("Content-Disposition", `attachment; filename="compliance-${req.params.id}.csv"`);
    res.send(header + rows);
  } catch (err) {
    next(err);
  }
});

export default router;
